<script setup lang="ts">
import { computed } from 'vue'
import { useShake } from '../../../composables/useShake'
import {
  FIELD_TEXT,
  FIELD_WELL,
  FIELD_WELL_DISABLED,
  FIELD_WELL_HOVER,
  FIELD_WELL_INVALID,
  FIELD_WELL_VALID,
} from '../control-surface'

const props = withDefaults(
  defineProps<{
    /** The input's value. v-model. */
    modelValue: string
    /** Native input type: `text` (default) · `email` · `url`. */
    type?: 'text' | 'email' | 'url'
    placeholder?: string
    /** Control size on the shared scale: `compact` (32px, small text, for a field inside a panel or a toolbar) · `md` (default) · `lg`. */
    size?: 'compact' | 'md' | 'lg'
    /** `error` paints the border and focus ring red; AppField drives this via `aria-invalid`. */
    state?: 'default' | 'error'
    disabled?: boolean
    /** Native `id`, usually supplied by AppField. */
    id?: string
    name?: string
    autocomplete?: string
    /** Native `inputmode`, selecting the on-screen keyboard. */
    inputmode?: 'text' | 'email' | 'url' | 'numeric' | 'tel' | 'search' | 'decimal' | 'none'
    /** Accessible name for standalone use (without an AppField label). */
    ariaLabel?: string
    /** Marks the field invalid to assistive tech and paints the error visuals. */
    ariaInvalid?: boolean
    /** Id of the element describing this control, usually supplied by AppField. */
    ariaDescribedby?: string
  }>(),
  {
    type: 'text',
    placeholder: '',
    size: 'md',
    state: 'default',
    disabled: false,
    id: undefined,
    name: undefined,
    autocomplete: undefined,
    inputmode: undefined,
    ariaLabel: undefined,
    ariaInvalid: undefined,
    ariaDescribedby: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  keydown: [event: KeyboardEvent]
}>()

const isError = computed(() => props.state === 'error' || props.ariaInvalid === true)

const SIZE_SCALE = {
  compact: 'h-16 gap-4 px-6',
  md: 'h-(--field-height) gap-4 px-6',
  lg: 'gap-4 px-6 py-6',
} as const

const TEXT_SCALE = {
  compact: FIELD_TEXT.replace('text-body-medium', 'text-body-sm-medium'),
  md: FIELD_TEXT,
  lg: FIELD_TEXT,
} as const

const { shaking, shakeIfInvalid, onAnimationEnd } = useShake(() => isError.value)

function onBlur(event: FocusEvent): void {
  shakeIfInvalid()
  emit('blur', event)
}

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div
    :class="[
      'group/input flex items-center',
      FIELD_WELL,
      SIZE_SCALE[size],
      isError ? FIELD_WELL_INVALID : FIELD_WELL_VALID,
      disabled ? FIELD_WELL_DISABLED : FIELD_WELL_HOVER,
      shaking ? 'motion-safe:animate-shake' : '',
    ]"
    @animationend="onAnimationEnd"
  >
    <span
      v-if="$slots.leading"
      class="flex shrink-0 items-center text-text-tertiary transition-colors duration-base ease-standard group-focus-within/input:text-accent-blue"
    >
      <slot name="leading" />
    </span>
    <input
      :id="id"
      :name="name"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :aria-label="ariaLabel"
      :aria-invalid="isError || undefined"
      :aria-describedby="ariaDescribedby"
      :class="['w-full min-w-0 flex-1', TEXT_SCALE[size]]"
      @input="onInput"
      @focus="emit('focus', $event)"
      @blur="onBlur"
      @keydown="emit('keydown', $event)"
    />
    <span v-if="$slots.trailing" class="flex shrink-0 items-center text-text-tertiary">
      <slot name="trailing" />
    </span>
  </div>
</template>
